import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PackagePlus, Trash2 } from 'lucide-react';
import { Section } from '@/shared/ui/layout/Section';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { Input } from '@/shared/ui/inputs/Input';
import { Select } from '@/shared/ui/selects/Select';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { useCategories } from '@/entities/categories';
import type {
  ParcelCreateProductDraft,
  ParcelCreateProductDraftErrors,
} from '@/features/parcel-create-products/modules/types';
import { makeEmptyDraft } from '@/features/parcel-create-products/modules/types';
import type { ApiCategoriesGetItem } from '@/shared/types/api/categories';

type LocalizedNames = { name_uz: string; name_ru: string; name_en: string };

// Inlined copy of parcel-products' ``categoryLabel`` so this feature
// doesn't reach across the features layer (FRONTEND_RULES).
const categoryLabel = (item: LocalizedNames, lang: string | undefined): string => {
  const code = (lang ?? 'uz').slice(0, 2);
  if (code === 'ru') return item.name_ru || item.name_uz;
  if (code === 'en') return item.name_en || item.name_uz;
  return item.name_uz;
};

type Props = {
  drafts: ParcelCreateProductDraft[];
  // Per-row validation errors keyed by ``client_id``. Empty/missing -> row
  // is valid (or not yet submitted, in which case errors are suppressed).
  errors: Record<string, ParcelCreateProductDraftErrors>;
  onChange: (drafts: ParcelCreateProductDraft[]) => void;
};

export const FeaturesParcelCreateInlineProducts = ({
  drafts,
  errors,
  onChange,
}: Props) => {
  const { t, i18n } = useTranslation();
  const categoriesQuery = useCategories();
  const categories: ApiCategoriesGetItem[] = useMemo(
    () => categoriesQuery.data ?? [],
    [categoriesQuery.data],
  );
  const lang = i18n.resolvedLanguage ?? i18n.language;

  const translateError = (msg?: string) => (msg ? t(msg) : undefined);

  const categoryOptions = useMemo(
    () => [
      { value: '', label: t('parcels.products.form.categoryPlaceholder') },
      ...categories.map((c) => ({ value: c.id, label: categoryLabel(c, lang) })),
    ],
    [categories, lang, t],
  );

  const subcategoryOptionsFor = (categoryId: string) => {
    const subs = categories.find((c) => c.id === categoryId)?.subcategories ?? [];
    return [
      { value: '', label: t('parcels.products.form.subcategoryPlaceholder') },
      ...subs.map((s) => ({ value: s.id, label: categoryLabel(s, lang) })),
    ];
  };

  const updateDraft = (
    clientId: string,
    patch: Partial<ParcelCreateProductDraft>,
  ) => {
    onChange(drafts.map((d) => (d.client_id === clientId ? { ...d, ...patch } : d)));
  };

  const removeDraft = (clientId: string) => {
    onChange(drafts.filter((d) => d.client_id !== clientId));
  };

  const addDraft = () => {
    onChange([...drafts, makeEmptyDraft()]);
  };

  const total = drafts.reduce((sum, d) => {
    const q = Number.parseFloat(d.quantity) || 0;
    const p = Number.parseFloat(d.price_per_item) || 0;
    return sum + q * p;
  }, 0);

  return (
    <Section
      title={t('parcels.products.title', { count: drafts.length })}
      action={
        <Button
          type="button"
          variant="outline"
          size="sm"
          leftIcon={<PackagePlus className="h-4 w-4" />}
          onClick={addDraft}
          data-cy="parcel-create-add-product"
        >
          {t('parcels.products.add')}
        </Button>
      }
    >
      {categoriesQuery.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : drafts.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('parcels.products.empty')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {drafts.map((draft, idx) => {
            const rowErrors = errors[draft.client_id] ?? {};
            return (
              <div
                key={draft.client_id}
                className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3"
                data-cy="parcel-create-product-row"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {t('parcels.products.inlineRow', { index: idx + 1 })}
                  </span>
                  <IconButton
                    label={t('parcels.products.delete')}
                    variant="danger"
                    onClick={() => removeDraft(draft.client_id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </IconButton>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Select
                    label={t('parcels.products.form.category')}
                    required
                    options={categoryOptions}
                    value={draft.category_id}
                    onChange={(e) =>
                      updateDraft(draft.client_id, {
                        category_id: e.target.value,
                        subcategory_id: '',
                      })
                    }
                    error={translateError(rowErrors.category_id)}
                  />
                  <Select
                    label={t('parcels.products.form.subcategory')}
                    required
                    options={subcategoryOptionsFor(draft.category_id)}
                    value={draft.subcategory_id}
                    onChange={(e) =>
                      updateDraft(draft.client_id, { subcategory_id: e.target.value })
                    }
                    disabled={!draft.category_id}
                    error={translateError(rowErrors.subcategory_id)}
                  />
                </div>
                <Input
                  label={t('parcels.products.form.fullName')}
                  required
                  value={draft.full_name}
                  onChange={(e) =>
                    updateDraft(draft.client_id, { full_name: e.target.value })
                  }
                  error={translateError(rowErrors.full_name)}
                />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input
                    label={t('parcels.products.form.quantity')}
                    required
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    value={draft.quantity}
                    onChange={(e) =>
                      updateDraft(draft.client_id, { quantity: e.target.value })
                    }
                    error={translateError(rowErrors.quantity)}
                  />
                  <Input
                    label={t('parcels.products.form.pricePerItem')}
                    required
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    value={draft.price_per_item}
                    onChange={(e) =>
                      updateDraft(draft.client_id, { price_per_item: e.target.value })
                    }
                    error={translateError(rowErrors.price_per_item)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {drafts.length > 0 && (
        <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-sm">
          <span className="text-ink-muted">{t('parcels.products.grandTotal')}</span>
          <span className="text-base font-semibold text-ink">
            {total.toLocaleString(undefined, {
              style: 'currency',
              currency: 'USD',
            })}
          </span>
        </div>
      )}
    </Section>
  );
};
